import type { SortDirection, TournamentSort } from '@brush/api-client';
import type { TournamentSortState } from '../../tournamentSort';

/**
 * The list's order, below `md`.
 *
 * This exists because the table's column headings *are* its sort control — four `SortHeader`s in
 * `<th>`s — so the phone arrangement that drops the table would drop sorting with it, silently.
 * The list is the one screen an operator scans by buy-in or by field size, so that is a loss worth
 * a control rather than a decision made by omission.
 *
 * **One select carrying both column and direction, not two controls.** A native `select` cannot
 * usefully shrink below about 150px, so a column picker beside a direction toggle never fits a
 * phone — and the pair would also let an operator ask for "Name, most first", which means nothing.
 * Each column offers only its two real ends, worded as the ends rather than as asc/desc.
 *
 * Each column's natural direction leads, matching `FIRST_DIRECTION` in `tournamentSort.ts`, so the
 * order a column opens in is the same whichever arrangement chose it.
 */
const OPTIONS: { column: TournamentSort; direction: SortDirection; label: string }[] = [
  { column: 'date', direction: 'asc', label: 'Date · soonest first' },
  { column: 'date', direction: 'desc', label: 'Date · latest first' },
  { column: 'name', direction: 'asc', label: 'Name · A to Z' },
  { column: 'name', direction: 'desc', label: 'Name · Z to A' },
  { column: 'buyIn', direction: 'desc', label: 'Buy-in · highest first' },
  { column: 'buyIn', direction: 'asc', label: 'Buy-in · lowest first' },
  { column: 'entries', direction: 'desc', label: 'Entries · most first' },
  { column: 'entries', direction: 'asc', label: 'Entries · fewest first' },
];

const keyOf = (s: TournamentSortState) => `${s.column}:${s.direction}`;

export function TournamentSortSelect({
  sort,
  onSort,
}: {
  sort: TournamentSortState;
  onSort: (next: TournamentSortState) => void;
}) {
  return (
    <label className="flex items-center gap-2 border-b border-ink-800/10 px-4 py-2.5 text-xs text-ink-800/60">
      Sort
      <select
        value={keyOf(sort)}
        onChange={(e) => {
          const picked = OPTIONS.find((o) => `${o.column}:${o.direction}` === e.target.value);
          // Ignore an unrecognised value rather than sorting by something the server would refuse:
          // an unknown sort column is a 400, and a silently unsorted list looks exactly like a
          // sorted one.
          if (picked) onSort({ column: picked.column, direction: picked.direction });
        }}
        className="min-w-0 flex-1 border border-ink-800/15 bg-white px-2 py-1.5 text-xs text-ink-950 outline-none focus:border-felt-500"
      >
        {OPTIONS.map((o) => (
          <option key={`${o.column}:${o.direction}`} value={`${o.column}:${o.direction}`}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
